import { useEffect, useRef, useState } from 'react'
import { ArrowRight, RefreshCw } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { appraisalInputPatch, editableAppraisalInputs } from '@/lib/appraisal-input-patch'
import { AppraisalEngineHeader } from '@/components/deals/appraisal/AppraisalEngineHeader'
import { AppraisalOutputPanel } from '@/components/deals/appraisal/AppraisalOutputPanel'
import { CostAssumptionsPanel } from '@/components/deals/appraisal/CostAssumptionsPanel'
import { FinanceStructurePanel } from '@/components/deals/appraisal/FinanceStructurePanel'
import { HoldingAssumptionsPanel } from '@/components/deals/appraisal/HoldingAssumptionsPanel'
import { SchemePanel } from '@/components/deals/appraisal/SchemePanel'
import { StrategyTargetingPanel } from '@/components/deals/appraisal/StrategyTargetingPanel'
import { TransactionTaxPanel } from '@/components/deals/appraisal/TransactionTaxPanel'
import {
  CONDITIONAL_COSTS,
  isCommercialUseType,
  isCoWorkStrategy,
  isServicedAccommodationStrategy,
} from '@/components/deals/appraisal/appraisal-data'
import {
  APPRAISAL_FORMULA_VERSION,
  buildResidualAppraisalResult,
  parseNumber,
  strategyFromConcept,
} from '@/components/deals/appraisal/appraisal-calculations'
import {
  normalizeTargetMetric,
  targetReturnError as validateTargetReturn,
} from '@/components/deals/appraisal/appraisal-targets'
import { inferTaxJurisdiction } from '@/components/deals/appraisal/appraisal-transaction-tax'
import {
  applyEvidenceChanges,
  blankEvidenceChanges,
  buildCommercialSchemeFromIncomeSchedule,
  buildMarketAwareAppraisalStates,
  changedEvidenceByTarget,
  commercialSchemeMatchesIncomeSchedule,
  hasManualEvidenceOverrides,
  projectSuggestions,
  refreshBrrrMarketData,
  refreshCommercialMarketData,
  refreshHmoMarketData,
} from '@/components/deals/appraisal/appraisal-market-data'
import { reviewDealEvidence } from '@/lib/deal-evidence-api'
import {
  createDealAppraisalSnapshot,
  getDealAppraisal,
  getDealEvidence,
  getDealEnrichment,
  updateDealAppraisal,
} from '@/lib/api'
import { looksPersistedDealId } from '@/lib/persisted-deal'
import { useSession } from '@/lib/session-context'

const DEFAULT_BASE_COST_VALUES = {
  'Refurb / Build Cost': '',
  'Contingency (% of refurb/build)': '',
  'Professional Fees': '',
  'Legal Costs': '',
  'Survey Costs': '',
}

const DEFAULT_HOLDING_STATE = {
  monthlyHoldingCosts: '',
  monthsToRefinance: '',
}

const DEFAULT_FINANCE_STATE = {
  purchaseInterestRatePa: '',
  purchaseLtvPercent: '',
  purchaseBrokerFee: '',
  purchaseArrangementFeePercent: '',
  purchaseValuationFee: '',
  developmentInterestRatePa: '',
  developmentLtcPercent: '',
  developmentBrokerFee: '',
  developmentArrangementFeePercent: '',
  developmentValuationFee: '',
  refinanceInterestRatePa: '',
  refinanceLtvPercent: '',
  refinanceBrokerFee: '',
  refinanceArrangementFeePercent: '',
  refinanceValuationFee: '',
}

const COMPARABLE_PROFILE_FIELDS = new Set(['propertyType', 'bedroomCount', 'internalArea'])
const DRAFT_PERSISTED_BRRR_FIELDS = new Set([
  ...COMPARABLE_PROFILE_FIELDS,
  'valuationMethod',
  'operatingModel',
])
const DRAFT_PERSISTED_COMMERCIAL_FIELDS = new Set([
  'units',
  'useType',
  'sizeSqft',
  'operatingModel',
  'valuationBasis',
])

export function DealAppraisalTab({
  deal,
  selectedStrategy,
  appraisalResults,
  defaultTarget = { metric: 'roce', targetReturn: '' },
  onCalculate,
  onAppraisalSaved,
  onReviewEvidence,
}) {
  const { currentUser, csrfToken } = useSession()
  const shouldPersist = Boolean(currentUser && looksPersistedDealId(deal?.id))
  const selectedCurrentStrategy = strategyFromConcept(selectedStrategy)
  const isCoWork = isCoWorkStrategy(selectedStrategy)
  const isSelectedServicedAccommodation = isServicedAccommodationStrategy(selectedStrategy)
  const postcodeArea = deal?.evidenceContext?.location?.postcodeDistrict ?? deal?.postcodeArea
  const initialSchemeStates = buildMarketAwareAppraisalStates({
    deal: { postcodeArea },
    selectedStrategy,
  })
  const [strategySelection, setStrategySelection] = useState({
    source: selectedCurrentStrategy,
    value: null,
  })
  const [isServicedAccommodationSetupEnabled, setIsServicedAccommodationSetupEnabled] = useState(() => (
    isSelectedServicedAccommodation
  ))
  const calculationTimerRef = useRef(null)
  const evidenceRefreshTimerRef = useRef(null)
  const evidenceRefreshGenerationRef = useRef(0)
  const revisionRef = useRef(0)
  const savedInputsRef = useRef({})
  const [savedInputs, setSavedInputs] = useState({})
  const loadedDealRef = useRef(null)
  const saveBlockedRef = useRef(false)
  const [manualReason, setManualReason] = useState('')
  const [isLoading, setIsLoading] = useState(shouldPersist)
  const draftSaveQueueRef = useRef(Promise.resolve())
  const isMountedRef = useRef(true)
  const initialTargetMetric = normalizeTargetMetric(defaultTarget?.metric) ?? 'roce'
  const initialTargetReturn = String(defaultTarget?.targetReturn ?? '').trim()
  const targetSourceRef = useRef(
    initialTargetMetric !== 'roce' || initialTargetReturn ? 'settings' : 'empty',
  )
  const [targetMetric, setTargetMetric] = useState(initialTargetMetric)
  const [targetReturn, setTargetReturn] = useState(initialTargetReturn)
  const [brrrState, setBrrrState] = useState(() => initialSchemeStates.brrr)
  const [hmoState, setHmoState] = useState(() => initialSchemeStates.hmo)
  const [commercialState, setCommercialState] = useState(() => initialSchemeStates.commercial)
  const schemeStatesRef = useRef(initialSchemeStates)
  const [baseCostValues, setBaseCostValues] = useState(DEFAULT_BASE_COST_VALUES)
  const [conditionalCostValues, setConditionalCostValues] = useState({})
  const [conditionalCostToggles, setConditionalCostToggles] = useState({})
  const [holdingState, setHoldingState] = useState(DEFAULT_HOLDING_STATE)
  const [financeState, setFinanceState] = useState(DEFAULT_FINANCE_STATE)
  const [transactionTaxState, setTransactionTaxState] = useState(() => (
    buildDefaultTransactionTaxState(postcodeArea, selectedCurrentStrategy)
  ))
  const [isCalculating, setIsCalculating] = useState(false)
  const [localResult, setLocalResult] = useState(null)
  const [error, setError] = useState(null)
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const [loadedEnrichment, setLoadedEnrichment] = useState(null)
  const [incomeScheduleStale, setIncomeScheduleStale] = useState(false)
  const [appraisalEvidenceState, setAppraisalEvidenceState] = useState(null)
  const [appraisalEvidence, setAppraisalEvidence] = useState(null)
  const [evidenceReviewConflict, setEvidenceReviewConflict] = useState(null)
  const [showReasonError, setShowReasonError] = useState(false)

  useEffect(() => {
    schemeStatesRef.current = { brrr: brrrState, hmo: hmoState, commercial: commercialState }
  }, [brrrState, commercialState, hmoState])

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false
      if (calculationTimerRef.current) {
        window.clearTimeout(calculationTimerRef.current)
        calculationTimerRef.current = null
      }
      evidenceRefreshGenerationRef.current += 1
      if (evidenceRefreshTimerRef.current) {
        window.clearTimeout(evidenceRefreshTimerRef.current)
        evidenceRefreshTimerRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (!['empty', 'settings'].includes(targetSourceRef.current)) return

    const nextTargetMetric = normalizeTargetMetric(defaultTarget?.metric) ?? 'roce'
    const nextTargetReturn = String(defaultTarget?.targetReturn ?? '').trim()
    targetSourceRef.current = nextTargetMetric !== 'roce' || nextTargetReturn ? 'settings' : 'empty'
    setTargetMetric(nextTargetMetric)
    setTargetReturn(nextTargetReturn)
  }, [defaultTarget?.metric, defaultTarget?.targetReturn])

  useEffect(() => {
    if (!shouldPersist) return undefined
    if (loadedDealRef.current === deal.id) return undefined

    let ignore = false

    Promise.all([
      getDealAppraisal(deal.id, { csrfToken }),
      getDealEnrichment(deal.id, { csrfToken }).catch(() => null),
      getDealEvidence(deal.id, { csrfToken }),
    ])
      .then(async ([payload, enrichment, evidencePayload]) => {
        if (ignore) return

        let currentInputs = payload.appraisal?.currentInputs
        let currentRevision = payload.appraisal?.currentInputsRevision ?? 0
        let evidence = evidencePayload.evidence
        let schemeStates = buildMarketAwareAppraisalStates({
          currentInputs,
          deal: { postcodeArea },
          enrichment,
          selectedStrategy,
          evidence,
        })
        const changes = blankEvidenceChanges(schemeStates, evidence)
        if (canReviewEvidence(evidence) && changes.length > 0) {
          try {
            const reviewPayload = await reviewDealEvidence(
              deal.id,
              evidenceAcceptanceRequest(evidence, changes, { stable: true }),
              { csrfToken },
            )
            const changedTargets = reviewPayload.review?.changedTargets ?? []
            schemeStates = applyEvidenceChanges(schemeStates, changedTargets)
            currentInputs = {
              ...(currentInputs ?? {}),
              scheme: applyEvidenceChanges(currentInputs?.scheme ?? {}, changedTargets),
            }
            currentRevision = reviewPayload.review?.resultingInputsRevision ?? currentRevision
            const refreshed = await getDealEvidence(deal.id, { csrfToken }).catch(() => null)
            evidence = refreshed?.evidence ?? evidence
          } catch (reviewError) {
            if (reviewError.status === 409) {
              setEvidenceReviewConflict(
                'Evidence or appraisal inputs changed. Reopen Appraisal to load the latest values.',
              )
            } else {
              setError(`Market evidence could not be applied: ${reviewError.message}`)
            }
          }
        }
        if (ignore) return

        revisionRef.current = currentRevision
        savedInputsRef.current = currentInputs ?? {}
        setSavedInputs(currentInputs ?? {})
        loadedDealRef.current = deal.id
        saveBlockedRef.current = false
        if (
          currentInputs?.strategy?.targetMetric !== undefined ||
          currentInputs?.strategy?.targetReturn !== undefined
        ) {
          targetSourceRef.current = 'persisted'
        }
        setLoadedEnrichment(enrichment)
        setAppraisalEvidence(evidence)
        setAppraisalEvidenceState(evidence?.analysisStates?.appraisal ?? null)
        const persistedCommercialUnits = currentInputs?.scheme?.commercial?.units
        setIncomeScheduleStale(
          Array.isArray(persistedCommercialUnits) &&
          !commercialSchemeMatchesIncomeSchedule(persistedCommercialUnits, enrichment?.units),
        )
        applyPersistedInputs(currentInputs, {
          selectedCurrentStrategy,
          setStrategySelection,
          setTargetMetric,
          setTargetReturn,
          setIsServicedAccommodationSetupEnabled,
          setBrrrState,
          setHmoState,
          setCommercialState,
          setBaseCostValues,
          setConditionalCostValues,
          setConditionalCostToggles,
          setHoldingState,
          setFinanceState,
          setTransactionTaxState,
        }, schemeStates, buildDefaultTransactionTaxState(postcodeArea, selectedCurrentStrategy))
        schemeStatesRef.current = schemeStates
        const latestOutput = payload.latestSnapshot?.outputSnapshot ?? payload.appraisal?.latestOutputs
        if (latestOutput && Object.keys(latestOutput).length > 0) setLocalResult(latestOutput)

        setIsLoading(false)
      })
      .catch((loadError) => {
        if (!ignore) { setError(loadError.message); setIsLoading(false); saveBlockedRef.current = true }
      })

    return () => { ignore = true }
  }, [
    csrfToken,
    deal?.id,
    postcodeArea,
    selectedCurrentStrategy,
    selectedStrategy,
    shouldPersist,
  ])

  function handleUpdateBaseCost(label, value) {
    setBaseCostValues((values) => ({ ...values, [label]: value }))
  }

  function handleToggleConditionalCost(name, isEnabled) {
    setConditionalCostToggles((toggles) => ({ ...toggles, [name]: isEnabled }))
    setConditionalCostValues((values) => ({
      ...values,
      [name]: values[name] ?? CONDITIONAL_COSTS[name]?.defaultValue ?? '',
    }))
  }

  function handleUpdateConditionalCost(name, value) {
    setConditionalCostValues((values) => ({ ...values, [name]: value }))
  }

  const isSAEnabled =
    isServicedAccommodationSetupEnabled ||
    brrrState.operatingModel === 'SA' ||
    isSelectedServicedAccommodation
  const currentStrategy = strategySelection.source === selectedCurrentStrategy
    ? strategySelection.value ?? selectedCurrentStrategy
    : selectedCurrentStrategy
  const result = localResult ?? appraisalResults
  const hasCalculated = Boolean(result)
  const calculateButtonLabel = buttonLabelForCalculation(isCalculating, hasCalculated)
  const usesCommercialScheme = currentStrategy === 'Commercial / Mixed-Use' ||
    currentStrategy === 'Commercial Conversion'
  const hasManualEvidenceOverride = hasManualEvidenceOverrides(
    { brrr: brrrState, hmo: hmoState, commercial: commercialState },
    savedInputs,
    appraisalEvidence,
  )
  const changedEvidence = changedEvidenceByTarget(
    { brrr: brrrState, hmo: hmoState, commercial: commercialState },
    appraisalEvidence,
  )

  function handleRefreshIncomeSchedule() {
    setCommercialState(buildCommercialSchemeFromIncomeSchedule({
      enrichment: loadedEnrichment,
      postcodeArea,
      selectedStrategy,
      currentState: commercialState,
    }))
    setIncomeScheduleStale(false)
    setLocalResult(null)
    setShowValidationErrors(false)
  }

  const handleCalculate = () => {
    if (
      validateTargetReturn(targetMetric, targetReturn) ||
      hasMissingMarketInputs(currentStrategy, { brrrState, hmoState, commercialState })
    ) {
      setShowValidationErrors(true)
      return
    }

    if (hasManualEvidenceOverride && !manualReason.trim()) {
      setShowReasonError(true)
      return
    }

    setShowValidationErrors(false)
    setShowReasonError(false)
    if (calculationTimerRef.current) {
      window.clearTimeout(calculationTimerRef.current)
    }

    setIsCalculating(true)
    calculationTimerRef.current = window.setTimeout(() => {
      if (!isMountedRef.current) {
        calculationTimerRef.current = null
        return
      }

      calculationTimerRef.current = null
      const inputs = currentInputs()
      const nextResult = buildResidualAppraisalResult(inputs)
      persistCalculation(nextResult)
    }, 300)
  }

  const currentInputs = ({ brrrStateOverride = brrrState } = {}) => buildAppraisalInputs({
    currentStrategy,
    targetMetric,
    targetReturn,
    selectedStrategy,
    isSAEnabled,
    brrrState: brrrStateOverride,
    hmoState,
    commercialState,
    baseCostValues,
    conditionalCostValues,
    conditionalCostToggles,
    holdingState,
    financeState,
    transactionTaxState,
  })

  function persistDraftInputs(inputs, { refreshEvidence = false } = {}) {
    if (!shouldPersist || saveBlockedRef.current) return Promise.resolve(null)
    const savePromise = draftSaveQueueRef.current.then(async () => {
      if (saveBlockedRef.current) return
      try {
        const payload = await updateDealAppraisal(deal.id, {
          currentInputs: inputs,
          expectedCurrentInputsRevision: revisionRef.current,
          activitySource: 'manual',
        }, { csrfToken })
        revisionRef.current = payload.appraisal.currentInputsRevision
        savedInputsRef.current = payload.appraisal.currentInputs
        setSavedInputs(payload.appraisal.currentInputs)
        if (refreshEvidence) {
          const generation = evidenceRefreshGenerationRef.current + 1
          evidenceRefreshGenerationRef.current = generation
          void refreshAppraisalEvidence({
            generation,
            minimumContextRevision: payload.analysisBasis?.context_revision ?? 0,
          })
        }
        return payload
      } catch (saveError) {
        saveBlockedRef.current = true
        if (isMountedRef.current) setError(saveError.message + '. Your edits are preserved. Reopen Appraisal to review saved values before retrying.')
      }
    })
    draftSaveQueueRef.current = savePromise
    return savePromise
  }

  function handleBrrrStateChange(nextState, { field, persist = true } = {}) {
    const refreshedState = refreshBrrrMarketData(brrrState, nextState)

    setBrrrState(refreshedState)
    if (persist && DRAFT_PERSISTED_BRRR_FIELDS.has(field)) {
      persistDraftInputs(
        { scheme: { brrr: { [field]: refreshedState[field] } } },
        { refreshEvidence: COMPARABLE_PROFILE_FIELDS.has(field) || field === 'operatingModel' },
      )
    }
  }

  function handleCommercialStateChange(nextState, { field, persist = true } = {}) {
    const refreshedState = refreshCommercialMarketData(commercialState, nextState)

    setCommercialState(refreshedState)
    if (persist && DRAFT_PERSISTED_COMMERCIAL_FIELDS.has(field)) {
      persistDraftInputs(
        { scheme: { commercial: { units: refreshedState.units } } },
        { refreshEvidence: true },
      )
    }
  }

  function handleTargetMetricChange(value) {
    targetSourceRef.current = 'user'
    setTargetMetric(value)
    setShowValidationErrors(false)
    persistDraftInputs({ strategy: { targetMetric: value, targetReturn } })
  }

  function handleTargetReturnBlur() {
    targetSourceRef.current = 'user'
    persistDraftInputs({ strategy: { targetMetric, targetReturn } })
  }

  async function handleEvidenceReview(request) {
    try {
      setEvidenceReviewConflict(null)
      const payload = await reviewDealEvidence(deal.id, request, { csrfToken })
      const changes = payload.review?.changedTargets ?? []
      const currentStates = { brrr: brrrState, hmo: hmoState, commercial: commercialState }
      const nextStates = applyEvidenceChanges(currentStates, changes)
      setBrrrState(nextStates.brrr)
      setHmoState(nextStates.hmo)
      setCommercialState(nextStates.commercial)
      const nextSavedInputs = {
        ...savedInputsRef.current,
        scheme: applyEvidenceChanges(savedInputsRef.current?.scheme ?? {}, changes),
      }
      savedInputsRef.current = nextSavedInputs
      setSavedInputs(nextSavedInputs)
      revisionRef.current = payload.review?.resultingInputsRevision ?? revisionRef.current
      setManualReason('')
      setShowReasonError(false)

      const refreshed = await getDealEvidence(deal.id, { csrfToken }).catch(() => null)
      if (refreshed?.evidence) {
        setAppraisalEvidence(refreshed.evidence)
        setAppraisalEvidenceState(refreshed.evidence.analysisStates?.appraisal ?? null)
      }
    } catch (reviewError) {
      if (reviewError.status === 409) {
        setEvidenceReviewConflict('Evidence or appraisal inputs changed. Close and reopen Appraisal before reviewing again.')
      } else {
        setError(`Market evidence review failed: ${reviewError.message}`)
      }
      throw reviewError
    }
  }

  async function refreshAppraisalEvidence({ generation, minimumContextRevision, maxAttempts = 15 }) {
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      if (!isMountedRef.current || generation !== evidenceRefreshGenerationRef.current) return

      try {
        const payload = await getDealEvidence(deal.id, { csrfToken })
        if (!isMountedRef.current || generation !== evidenceRefreshGenerationRef.current) return

        let evidence = payload.evidence
        const evidenceContextRevision = evidence?.context?.revision
        const evidenceInputsRevision = evidence?.adoptedBasis?.currentInputsRevision
        const staleContext = Number.isInteger(evidenceContextRevision) &&
          evidenceContextRevision < minimumContextRevision
        const staleInputs = Number.isInteger(evidenceInputsRevision) &&
          evidenceInputsRevision < revisionRef.current
        if (!staleContext && !staleInputs) {
          let nextStates = projectSuggestions(schemeStatesRef.current, evidence)
          setEvidenceState(evidence, nextStates)
          const blankChanges = blankEvidenceChanges(nextStates, evidence)
          if (canReviewEvidence(evidence) && blankChanges.length > 0) {
            const reviewPayload = await reviewDealEvidence(
              deal.id,
              evidenceAcceptanceRequest(evidence, blankChanges, { stable: true }),
              { csrfToken },
            )
            const changedTargets = reviewPayload.review?.changedTargets ?? []
            nextStates = applyEvidenceChanges(nextStates, changedTargets)
            schemeStatesRef.current = nextStates
            setSchemeStates(nextStates)
            revisionRef.current = reviewPayload.review?.resultingInputsRevision ?? revisionRef.current
            const nextSavedInputs = {
              ...savedInputsRef.current,
              scheme: applyEvidenceChanges(savedInputsRef.current?.scheme ?? {}, changedTargets),
            }
            savedInputsRef.current = nextSavedInputs
            setSavedInputs(nextSavedInputs)
            const refreshed = await getDealEvidence(deal.id, { csrfToken })
            if (!isMountedRef.current || generation !== evidenceRefreshGenerationRef.current) return
            evidence = refreshed.evidence
            setAppraisalEvidence(evidence)
            setAppraisalEvidenceState(evidence?.analysisStates?.appraisal ?? null)
          }
          if (!evidence?.availability?.refreshPending) return
        }
      } catch (refreshError) {
        if (isMountedRef.current && generation === evidenceRefreshGenerationRef.current) {
          setError(`Market evidence refresh failed: ${refreshError.message}`)
        }
        return
      }

      await waitForEvidenceRefresh(generation)
    }
  }

  function setEvidenceState(evidence, states) {
    schemeStatesRef.current = states
    setSchemeStates(states)
    setAppraisalEvidence(evidence)
    setAppraisalEvidenceState(evidence?.analysisStates?.appraisal ?? null)
  }

  function setSchemeStates(states) {
    setBrrrState(states.brrr)
    setHmoState(states.hmo)
    setCommercialState(states.commercial)
  }

  function waitForEvidenceRefresh(generation) {
    return new Promise((resolve) => {
      evidenceRefreshTimerRef.current = window.setTimeout(() => {
        evidenceRefreshTimerRef.current = null
        resolve(generation === evidenceRefreshGenerationRef.current)
      }, 1_000)
    })
  }

  function handleUseLatestEvidence(suggestion) {
    const change = {
      target: suggestion.target,
      suggestionId: suggestion.suggestionId,
      value: String(suggestion.suggestedValue ?? ''),
      unit: suggestion.unit,
    }
    void handleEvidenceReview(evidenceAcceptanceRequest(appraisalEvidence, [change])).catch(() => {})
  }

  function handleKeepAdoptedEvidence(suggestion) {
    void handleEvidenceReview(evidenceKeepRequest(appraisalEvidence, suggestion.suggestionId)).catch(() => {})
  }

  const persistCalculation = async (nextResult) => {
    if (!shouldPersist) {
      setLocalResult(nextResult)
      setIsCalculating(false)
      onCalculate?.(nextResult.maxPrice, nextResult.targetClose, nextResult)
      return
    }

    try {
      setError(null)
      await draftSaveQueueRef.current
      if (saveBlockedRef.current) throw new Error('Saved inputs changed or save status is uncertain. Your edits are preserved; review saved values before retrying.')
      const inputs = editableAppraisalInputs(currentInputs())
      const saved = await updateDealAppraisal(deal.id, {
        selectedStrategyId: selectedStrategy?.persistedId,
        currentInputs: appraisalInputPatch(inputs, savedInputsRef.current),
        expectedCurrentInputsRevision: revisionRef.current,
        reason: manualReason,
        activitySource: 'manual',
      }, { csrfToken })
      revisionRef.current = saved.appraisal.currentInputsRevision
      savedInputsRef.current = saved.appraisal.currentInputs
      setSavedInputs(saved.appraisal.currentInputs)
      const payload = await createDealAppraisalSnapshot(deal.id, {
        selectedStrategyId: selectedStrategy?.persistedId,
        inputSnapshot: saved.appraisal.currentInputs,
        expectedCurrentInputsRevision: revisionRef.current,
        expectedContextRevision: saved.analysisBasis?.context_revision,
        evidenceSnapshotId: saved.analysisBasis?.snapshot_id,
        outputSnapshot: nextResult,
        assumptionSnapshot: inputs,
        strategySnapshot: selectedStrategy ?? {},
        formulaKey: nextResult.formulaKey ?? 'residual_roce',
        formulaVersion: nextResult.formulaVersion ?? APPRAISAL_FORMULA_VERSION,
        activitySource: 'system',
      }, { csrfToken })
      const persistedResult = payload.latestSnapshot?.outputSnapshot ?? nextResult
      setLocalResult(persistedResult)
      onAppraisalSaved?.({
        deal: payload.deal,
        snapshot: payload.latestSnapshot ?? payload.createdSnapshot,
        result: persistedResult,
      })
    } catch (saveError) {
      if (saveError.status === 409) saveBlockedRef.current = true
      setError(saveError.status === 409 ? 'Inputs or evidence changed. Your edits are preserved; review saved values before retrying.' : saveError.message)
    } finally {
      setIsCalculating(false)
    }
  }

  return (
    <div>
      <AppraisalEngineHeader />

      {error ? (
        <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive">
          {error}
        </div>
      ) : null}

      {appraisalEvidenceState?.status === 'review_needed' ? (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3">
          <div>
            <p className="text-sm font-bold text-foreground">Appraisal evidence review required</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Evidence or assumptions changed. Saved calculation has not been replaced.
            </p>
          </div>
          {onReviewEvidence ? (
            <Button type="button" variant="outline" size="sm" onClick={onReviewEvidence}>
              Review Market Evidence
            </Button>
          ) : null}
        </div>
      ) : null}

      {evidenceReviewConflict ? (
        <p role="alert" className="mb-4 rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {evidenceReviewConflict}
        </p>
      ) : null}

      <fieldset disabled={isLoading || isCalculating} className="grid min-w-0 gap-5 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <section className="space-y-5">
          <StrategyTargetingPanel
            currentStrategy={currentStrategy}
            targetMetric={targetMetric}
            targetReturn={targetReturn}
            onCurrentStrategyChange={(value) => {
              setStrategySelection({ source: selectedCurrentStrategy, value })
              persistDraftInputs(
                { strategy: { currentStrategy: value } },
                { refreshEvidence: true },
              )
            }}
            onTargetMetricChange={handleTargetMetricChange}
            onTargetReturnChange={(value) => {
              targetSourceRef.current = 'user'
              setTargetReturn(value)
              setShowValidationErrors(false)
            }}
            onTargetReturnBlur={handleTargetReturnBlur}
            targetReturnError={showValidationErrors ? validateTargetReturn(targetMetric, targetReturn) : ''}
          />
          <TransactionTaxPanel
            postcodeArea={postcodeArea}
            state={transactionTaxState}
            onStateChange={setTransactionTaxState}
          />
          {usesCommercialScheme && incomeScheduleStale ? (
            <div className="flex flex-wrap items-center justify-between gap-4 border-y border-primary/30 bg-primary/5 px-5 py-4">
              <div>
                <p className="text-sm font-bold text-foreground">Income schedule changed</p>
                <p className="mt-1 text-xs font-medium text-muted-foreground">
                  Refresh scheme units when ready. Saved appraisal edits remain unchanged until then.
                </p>
              </div>
              <Button type="button" variant="outline" onClick={handleRefreshIncomeSchedule}>
                <RefreshCw aria-hidden="true" className="h-4 w-4" />
                Refresh from Income Schedule
              </Button>
            </div>
          ) : null}
          <SchemePanel
            currentStrategy={currentStrategy}
            brrrState={brrrState}
            hmoState={hmoState}
            commercialState={commercialState}
            isCoWork={isCoWork}
            postcodeArea={postcodeArea}
            showValidationErrors={showValidationErrors}
            onBrrrStateChange={handleBrrrStateChange}
            onHmoStateChange={(nextState) => {
              setHmoState((previousState) => (
                refreshHmoMarketData(previousState, nextState)
              ))
            }}
            onCommercialStateChange={handleCommercialStateChange}
            changedEvidence={changedEvidence}
            onUseLatestEvidence={handleUseLatestEvidence}
            onKeepAdoptedEvidence={handleKeepAdoptedEvidence}
          />
          <CostAssumptionsPanel
            currentStrategy={currentStrategy}
            isSAEnabled={isSAEnabled}
            baseCostValues={baseCostValues}
            conditionalCostValues={conditionalCostValues}
            conditionalCostToggles={conditionalCostToggles}
            onUpdateBaseCost={handleUpdateBaseCost}
            onServicedAccommodationChange={setIsServicedAccommodationSetupEnabled}
            onToggleConditionalCost={handleToggleConditionalCost}
            onUpdateConditionalCost={handleUpdateConditionalCost}
          />
          <HoldingAssumptionsPanel
            state={holdingState}
            onStateChange={setHoldingState}
          />
          <FinanceStructurePanel
            state={financeState}
            onStateChange={setFinanceState}
          />
          {shouldPersist && hasManualEvidenceOverride ? (
            <div className="space-y-2">
              <label htmlFor="appraisal-manual-reason" className="text-sm font-medium">Reason for overriding market evidence</label>
              <Textarea id="appraisal-manual-reason" value={manualReason} maxLength={2000}
                aria-invalid={showReasonError && !manualReason.trim()}
                onChange={(event) => { setManualReason(event.target.value); setShowReasonError(false) }}
                placeholder="Explain the evidence or judgement behind changed adopted values." />
              <p className="text-xs text-muted-foreground">Required because an eligible market suggestion exists. Original evidence remains unchanged.</p>
              {showReasonError && !manualReason.trim() ? (
                <p role="alert" className="text-xs font-medium text-destructive">Enter a reason before calculating.</p>
              ) : null}
            </div>
          ) : null}
          <div className="pt-4">
            <p className="mb-2 text-center text-xs font-medium text-muted-foreground">
              Based on current assumptions
            </p>
            <Button
              type="button"
              disabled={isCalculating}
              onClick={handleCalculate}
              className="group h-auto w-full rounded-xl py-4 text-base font-bold shadow-md disabled:opacity-80"
            >
              {calculateButtonLabel}
              {!isCalculating ? (
                <ArrowRight
                  aria-hidden="true"
                  data-testid="appraisal-calculate-arrow"
                  className="h-5 w-5 transition-transform group-hover:translate-x-1"
                />
              ) : null}
            </Button>
          </div>
        </section>

        <div className="relative">
          <AppraisalOutputPanel
            hasCalculated={hasCalculated}
            selectedStrategy={selectedStrategy}
            result={result}
            targetMetric={targetMetric}
            targetReturn={targetReturn}
          />
        </div>
      </fieldset>
    </div>
  )
}

function canReviewEvidence(evidence) {
  return Boolean(
    evidence?.availableSnapshot?.id &&
    Number.isInteger(evidence?.context?.revision) &&
    Number.isInteger(evidence?.adoptedBasis?.currentInputsRevision),
  )
}

function evidenceAcceptanceRequest(evidence, changes, { stable = false } = {}) {
  const suggestionIds = changes.map((change) => change.suggestionId).sort()
  return {
    idempotencyKey: stable
      ? `appraisal-evidence-${evidence.availableSnapshot.id}-${evidence.adoptedBasis.currentInputsRevision}-${shortHash(suggestionIds.join('|'))}`
      : `appraisal-evidence-${crypto.randomUUID()}`,
    snapshotId: evidence.availableSnapshot.id,
    expectedContextRevision: evidence.context.revision,
    expectedCurrentInputsRevision: evidence.adoptedBasis.currentInputsRevision,
    decision: 'accept',
    reviewedSuggestionIds: evidence.suggestions.map((suggestion) => suggestion.id),
    reason: '',
    changes,
  }
}

function shortHash(value) {
  let hash = 0
  for (const character of value) {
    hash = (hash * 31 + character.charCodeAt(0)) % 2_147_483_647
  }
  return hash.toString(36)
}

function evidenceKeepRequest(evidence, suggestionId) {
  return {
    idempotencyKey: `appraisal-evidence-${crypto.randomUUID()}`,
    snapshotId: evidence.availableSnapshot.id,
    expectedContextRevision: evidence.context.revision,
    expectedCurrentInputsRevision: evidence.adoptedBasis.currentInputsRevision,
    decision: 'keep',
    reviewedSuggestionIds: [suggestionId],
    reason: 'Kept existing adopted value after reviewing updated market evidence',
    changes: [],
  }
}

function buttonLabelForCalculation(isCalculating, hasCalculated) {
  if (isCalculating) return 'Calculating...'
  if (hasCalculated) return 'Recalculate Appraisal'

  return 'Calculate Maximum Purchase Price'
}

function hasMissingMarketInputs(currentStrategy, { brrrState, hmoState, commercialState }) {
  if (currentStrategy === 'HMO') {
    if (hmoState.rooms.some((room) => isBlank(room.adoptedRent))) return true
    if (hmoState.valuationMethod === 'HMO Comparable') return isBlank(hmoState.hmoAdoptedValue)
    if (hmoState.valuationMethod === 'Bricks & Mortar (£/sqft)') return isBlank(hmoState.adoptedSqft)

    return false
  }

  if (currentStrategy === 'Commercial / Mixed-Use' || currentStrategy === 'Commercial Conversion') {
    return commercialState.units.some((unit) => {
      if (isCommercialUseType(unit.useType)) return isBlank(unit.adoptedSqftYear)
      if (unit.operatingModel === 'AST' && isBlank(unit.adoptedMonthlyRent)) return true
      if (unit.operatingModel === 'SA' && isBlank(unit.adoptedAdr)) return true
      if (unit.valuationBasis === 'Comparable') return isBlank(unit.adoptedValue)
      if (unit.valuationBasis === '£/sqft') return isBlank(unit.adoptedSqftValue)

      return false
    })
  }

  if (brrrState.valuationMethod === 'Comparable' && isBlank(brrrState.adoptedGdv)) return true
  if (brrrState.valuationMethod === '£/sqft' && isBlank(brrrState.adoptedSqft)) return true
  if (brrrState.exitMode === 'Sell') return false
  if (brrrState.operatingModel === 'AST') return isBlank(brrrState.adoptedMonthlyRent)

  return isBlank(brrrState.adoptedAdr)
}

function isBlank(value) {
  return String(value ?? '').trim() === ''
}

function buildAppraisalInputs({
  currentStrategy,
  targetMetric,
  targetReturn,
  selectedStrategy,
  isSAEnabled,
  brrrState,
  hmoState,
  commercialState,
  baseCostValues,
  conditionalCostValues,
  conditionalCostToggles,
  holdingState,
  financeState,
  transactionTaxState,
}) {
  return {
    strategy: {
      currentStrategy,
      targetMetric,
      targetReturn,
      selectedStrategy,
    },
    scheme: {
      servicedAccommodationEnabled: isSAEnabled,
      brrr: brrrState,
      hmo: hmoState,
      commercial: commercialState,
    },
    costs: {
      baseCostValues,
      conditionalCostValues,
      conditionalCostToggles,
    },
    holding: holdingState,
    finance: financeState,
    transactionTax: transactionTaxState,
  }
}

function applyPersistedInputs(inputs, setters, schemeStates, defaultTransactionTaxState) {
  inputs ??= {}
  const strategy = inputs.strategy ?? {}
  const costs = inputs.costs ?? {}

  setters.setBrrrState(schemeStates.brrr)
  setters.setHmoState(schemeStates.hmo)
  setters.setCommercialState(schemeStates.commercial)

  if (strategy.currentStrategy) {
    setters.setStrategySelection({
      source: setters.selectedCurrentStrategy,
      value: strategy.currentStrategy,
    })
  }
  if (strategy.targetMetric !== undefined) {
    setters.setTargetMetric(normalizeTargetMetric(strategy.targetMetric) ?? strategy.targetMetric)
  }
  if (strategy.targetReturn !== undefined) setters.setTargetReturn(strategy.targetReturn)
  if (inputs.scheme?.servicedAccommodationEnabled !== undefined) {
    setters.setIsServicedAccommodationSetupEnabled(Boolean(inputs.scheme.servicedAccommodationEnabled))
  }
  if (costs.baseCostValues) setters.setBaseCostValues(costs.baseCostValues)
  if (costs.conditionalCostValues) setters.setConditionalCostValues(costs.conditionalCostValues)
  if (costs.conditionalCostToggles) setters.setConditionalCostToggles(costs.conditionalCostToggles)
  if (inputs.holding) setters.setHoldingState(inputs.holding)
  if (inputs.finance) {
    const finance = annualFinanceInputs(inputs.finance)
    setters.setFinanceState(Object.fromEntries(
      Object.keys(DEFAULT_FINANCE_STATE).map((key) => [key, finance[key] ?? '']),
    ))
  }
  setters.setTransactionTaxState({
    ...defaultTransactionTaxState,
    ...(inputs.transactionTax ?? {}),
  })
}

function annualFinanceInputs(finance) {
  return {
    ...finance,
    purchaseInterestRatePa: annualInterestRate(
      finance.purchaseInterestRatePa,
      finance.purchaseInterestRatePcm,
    ),
    developmentInterestRatePa: annualInterestRate(
      finance.developmentInterestRatePa,
      finance.developmentInterestRatePcm,
    ),
    refinanceArrangementFeePercent: finance.refinanceArrangementFeePercent ??
      finance.refinanceLenderFeePercent ??
      '',
  }
}

function annualInterestRate(annualRate, monthlyRate) {
  if (!isBlank(annualRate)) return annualRate
  if (isBlank(monthlyRate)) return ''

  return String(Number((parseNumber(monthlyRate) * 12).toFixed(4)))
}

function buildDefaultTransactionTaxState(postcodeArea, strategy) {
  const isNonResidential = strategy === 'Commercial / Mixed-Use' ||
    strategy === 'Commercial Conversion'

  return {
    jurisdiction: inferTaxJurisdiction(postcodeArea),
    propertyType: isNonResidential ? 'non_residential' : 'residential',
    additionalResidentialProperty: false,
  }
}
